import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Highlight } from '@client/components/_ui/display/Highlight';
import { RichText } from '@client/components/_ui/display/RichText';
import { EmptyState } from '@client/components/_ui/feedback/EmptyState';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { ExternalLink } from '@client/components/Common/Link';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { queryClient, trpcOptions } from '@client/trpc/client';
import { firstQueryError, isRetryingQueries, retryFailedQueries } from '@client/trpc/query-retry';
import { highlightTextNodes } from '@client/utils/ui/highlight-text-nodes';
import { SUPPORT_EMAIL } from '@shared/constants/system/email';
import { MAX_PAGE_SIZE } from '@shared/constants/system/general';
import { keepPreviousData, skipToken, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Collapse, Input, Select, Tag } from 'antd';
import clsx from 'clsx';
import { ChevronRight, HelpCircle, Search } from 'lucide-react';
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { afterLayoutSettles } from './after-layout-settles';
import {
    buildFaqWhere,
    FAQ_MAX_CATEGORIES,
    FAQ_SEARCH_MAX_LENGTH,
    type FaqSearch,
    FaqSearchSchema,
    faqCategoryOptions,
    faqFilterKey,
    faqPagesAgree,
    flattenFaqPages,
    nextFaqPageOffset,
} from './faq-search';
import { useSettledSearch } from './useSettledSearch';

// react-player ships adapters for HLS (~496 KB), DASH (~940 KB), MUX (~504 KB)
// and 7 other platforms. Lazy-load so the FAQ route chunk doesn't ship them
// to every user who navigates here — they're only fetched when a video card
// actually renders.
const ReactPlayer = lazy(() => import('react-player'));

export const Route = createFileRoute('/console/$orgId/faq')({
    staticData: {
        // Open to every console user (CUR-2690), but not to portal-only fleet users: the FAQ
        // procedures stay off the portal shell (CUR-1049), and the help menu hides FAQ for them.
        consoleWorkspace: true,
        breadcrumb: {
            title: 'FAQ',
        },
        ui: {
            title: 'Frequently asked questions',
            subtitle: 'Find answers to common questions about using the platform',
            icon: HelpCircle,
        },
    },
    validateSearch: zodValidator(FaqSearchSchema),
    component: FaqPage,
});

const MATCH_CLASS_NAME = 'rounded-sm bg-warning/25 px-0.5 text-foreground';
const NO_CATEGORIES: string[] = [];

function VideoPlayer({ videoFileKey }: { videoFileKey?: string }) {
    const { data, isLoading } = useQuery(
        trpcOptions.file.getDownloadUrl.queryOptions(
            videoFileKey
                ? {
                      key: videoFileKey,
                  }
                : skipToken,
        ),
    );

    if (!videoFileKey) {
        return null;
    }

    return (
        <Loading spinning={isLoading} size="large" description="Loading video..." keepWrapper>
            <div className="mt-4 min-h-20 max-w-2xl overflow-hidden rounded-lg">
                <Suspense fallback={null}>
                    <ReactPlayer src={data} controls width="100%" height="100%" />
                </Suspense>
            </div>
        </Loading>
    );
}

/** An answer with each occurrence of the search term marked in its rendered text. */
function FaqAnswer({ content, term }: { content: string; term: string }) {
    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (containerRef.current) {
            highlightTextNodes(containerRef.current, term, MATCH_CLASS_NAME);
        }
    }, [content, term]);

    return (
        <div ref={containerRef}>
            <RichText data={content} className="prose prose-sm max-w-prose" format="tiptap" />
        </div>
    );
}

function FaqPage() {
    const search = Route.useSearch();
    const { orgId } = Route.useParams();
    const navigate = useNavigate();

    const query = search.q ?? '';
    const categories = search.category ?? NO_CATEGORIES;
    const openId = search.open;

    const setSearch = (next: Partial<FaqSearch>, { replace = false }: { replace?: boolean } = {}) =>
        navigate({
            to: '/console/$orgId/faq',
            params: { orgId },
            search: { ...search, ...next },
            replace,
            resetScroll: false,
        });

    const filterKey = faqFilterKey({ q: query, category: categories });
    // The filter change the user has just made on this page, from the filter it replaces, until
    // its results settle. Only such a change may open a single match (see the auto-open below).
    const autoOpenRef = useRef<{ from: string; to: string; reached: boolean } | null>(null);
    const changeFilter = (next: Pick<FaqSearch, 'q' | 'category'>) => {
        const to = faqFilterKey({ q: query, category: categories, ...next });
        autoOpenRef.current = { from: filterKey, to, reached: false };
        // A new filter starts closed; a single match opens through the effect below.
        setSearch({ ...next, open: undefined });
    };

    // The input answers every keystroke, and follows the URL when Back, forward or a shared link
    // changes it. The URL (and so the request) follows once typing pauses. Each settled search is
    // its own history entry, so Back returns to the search before it. Back, forward and jumps
    // restore a history entry; the page's own writes (push, replace) do not. Each restoration drops
    // an edit still waiting to settle, even when the entry has the same search and differs only in
    // its open question or categories: settling it would write over the restored view and cut off
    // Forward. A question clicked while typing is the page's own write, so the search still settles.
    const { history } = useRouter();
    const [restorations, setRestorations] = useState(0);
    useEffect(
        () =>
            history.subscribe(({ action }) => {
                if (action.type !== 'PUSH' && action.type !== 'REPLACE') {
                    setRestorations((count) => count + 1);
                }
            }),
        [history],
    );
    const { text: searchText, setText: setSearchText, settled: settledSearch } = useSettledSearch(query, restorations);
    const latestQueryRef = useRef(query);
    latestQueryRef.current = query;
    useEffect(() => {
        if (settledSearch !== undefined && settledSearch !== latestQueryRef.current) {
            changeFilter({ q: settledSearch || undefined });
        }
        // Only a settled search writes the URL; a URL change (Back) must not write it back.
    }, [settledSearch]);

    const where = useMemo(() => buildFaqWhere({ q: query, category: categories }), [query, categories]);
    const baseInput = useMemo(() => ({ limit: MAX_PAGE_SIZE, where }), [where]);

    // The server pages at MAX_PAGE_SIZE, so the list loads a page at a time ("Load more"), up to
    // the server's offset limit.
    // Keyed under the procedure's path so `pathFilter()` invalidation still reaches it.
    const faqsQuery = useInfiniteQuery({
        queryKey: [...trpcOptions.faq.listFaqs.queryKey(baseInput), 'pages'],
        queryFn: ({ pageParam }) =>
            queryClient.fetchQuery(
                trpcOptions.faq.listFaqs.queryOptions(
                    { ...baseInput, offset: pageParam },
                    // The page query below owns retries and the inline error.
                    { retry: false, meta: { errorSurface: 'inline' } },
                ),
            ),
        initialPageParam: 0,
        getNextPageParam: (_lastPage, pages) => nextFaqPageOffset(pages),
        placeholderData: keepPreviousData,
        // A refetch requests every loaded page in turn, so returning to the tab does not start one.
        // Mounting still does: the linked-question loader below waits for that refresh.
        refetchOnWindowFocus: false,
        meta: { errorSurface: 'inline' },
    });
    const categoriesQuery = useQuery({
        ...trpcOptions.faq.getFaqCategories.queryOptions(),
        meta: { errorSurface: 'inline' },
    });
    const {
        data,
        isPending,
        isFetching,
        isRefetching,
        isPlaceholderData,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
    } = faqsQuery;
    // Any failure, a "Load more" page's included, stops the linked-question loader below.
    const anyQueryError = firstQueryError([faqsQuery, categoriesQuery]);
    // A failed "Load more" page keeps the loaded questions on screen, with its own error and retry
    // beside the button, and so does a refetch of the loaded pages after one. Only a list that
    // failed outright (or the categories) replaces the page with an error.
    const listError = isFetchNextPageError || (isRefetching && data !== undefined) ? null : faqsQuery.error;
    const pageQueries = [{ error: listError, isFetching, refetch: faqsQuery.refetch }, categoriesQuery];
    const queryError = firstQueryError(pageQueries);

    const rows = useMemo(() => flattenFaqPages(data?.pages ?? []), [data]);
    // The last page loaded is the latest count, and the one "Load more" pages against.
    const total = data?.pages.at(-1)?.total ?? 0;
    const pagesAgree = faqPagesAgree(data?.pages ?? []);
    const openIsLoaded = openId !== undefined && rows.some((faq) => String(faq.id) === openId);

    // A question published or unpublished between two pages shifts the later one, and after an
    // unpublish the question that slid back onto an earlier page is in neither. When the pages'
    // totals disagree, load every page again from the start. Once for each disagreement until the
    // pages agree: a server that answered the same disagreement again would otherwise reload
    // without end.
    const reloadedForRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!data || isPlaceholderData || isFetching || anyQueryError) {
            return;
        }
        if (pagesAgree) {
            reloadedForRef.current = undefined;
            return;
        }
        const disagreement = `${filterKey} ${data.pages.map((page) => page.total).join(',')}`;
        if (reloadedForRef.current !== disagreement) {
            reloadedForRef.current = disagreement;
            void faqsQuery.refetch();
        }
    }, [data, pagesAgree, isPlaceholderData, isFetching, anyQueryError]);

    // A shared link can open a question beyond the first page: load on until it appears. When
    // it never does (unpublished, another audience, filtered out), drop it from the URL. Any fetch
    // in flight is waited for first: a cached list can predate the question while it refreshes.
    // So are pages that disagree, which reload from the start (above).
    useEffect(() => {
        if (!openId || openIsLoaded || isPending || isPlaceholderData || isFetching || !pagesAgree || anyQueryError) {
            return;
        }
        if (hasNextPage) {
            void fetchNextPage();
        } else {
            setSearch({ open: undefined }, { replace: true });
        }
    }, [openId, openIsLoaded, isPending, isPlaceholderData, isFetching, pagesAgree, hasNextPage, anyQueryError]);

    // The open question last scrolled to, or opened in place, so each is scrolled to at most once.
    const scrolledToRef = useRef<string | undefined>(undefined);
    // Once nothing is open (closed by a click, a filter change, Clear filters or a dropped link),
    // a question that Back or a link opens again is scrolled to again. Declared before the
    // auto-open below, which marks its own question in the same commit.
    useEffect(() => {
        if (!openId) {
            scrolledToRef.current = undefined;
        }
    }, [openId]);

    // When the user's own search or category change leaves exactly one question, open it, once,
    // so the user can still close it. Restoring a view (Back, forward, refresh, a link) never
    // does: its URL already says which question is open, and one the user closed stays closed.
    useEffect(() => {
        const pending = autoOpenRef.current;
        if (!pending) {
            return;
        }
        if (filterKey !== pending.to) {
            // Until the URL carries the new filter its old one is still showing. Once it has, a
            // move elsewhere (Back, forward) restores that view as it is.
            if (pending.reached || filterKey !== pending.from) {
                autoOpenRef.current = null;
            }
            return;
        }
        pending.reached = true;
        // A cached list for the filter can predate it while it refreshes: wait for the refresh. One
        // that fails leaves the cached rows on hand, so it ends the intent without opening any.
        if (!data || isPlaceholderData || isFetching) {
            return;
        }
        autoOpenRef.current = null;
        const [only] = rows;
        if (!anyQueryError && where && total === 1 && only && String(only.id) !== openId) {
            scrolledToRef.current = String(only.id);
            setSearch({ open: String(only.id) }, { replace: true });
        }
    }, [data, isPlaceholderData, isFetching, anyQueryError, filterKey]);

    // An open question that arrives through the URL (shared link, refresh, Back) is scrolled
    // into view once its row renders. Opening one by clicking leaves the page where it is.
    // Previous rows held on screen while a list loads may hold the question elsewhere, and so may
    // a cached list while it refreshes: questions published since push it down, even onto a page
    // the loader above has still to fetch. So the scroll waits until the list has settled, and then
    // until the accordion has: a tall answer above that is closing still holds the question lower
    // down until its animation ends. A question opened or closed meanwhile cancels the wait.
    const listRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (
            !openId ||
            !openIsLoaded ||
            isPlaceholderData ||
            isFetching ||
            !pagesAgree ||
            scrolledToRef.current === openId
        ) {
            return;
        }
        const header = () => document.getElementById(`faq-${openId}`);
        return afterLayoutSettles(
            () => {
                const [question, list] = [header(), listRef.current];
                if (!question || !list) return '';
                const listBox = list.getBoundingClientRect();
                return `${question.getBoundingClientRect().top - listBox.top} ${listBox.height}`;
            },
            () => {
                scrolledToRef.current = openId;
                header()?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            },
        );
    }, [openId, openIsLoaded, isPlaceholderData, isFetching, pagesAgree]);

    const handleOpenChange = (keys: string | string[]) => {
        const [key] = Array.isArray(keys) ? keys : [keys];
        scrolledToRef.current = key;
        setSearch({ open: key || undefined });
    };

    const clearFilters = () => {
        setSearchText('');
        setSearch({ q: undefined, category: undefined, open: undefined });
    };

    // Memoised so typing in the search box, which re-renders the page, leaves the loaded rows alone.
    const highlights = useMemo(() => (query ? [query] : undefined), [query]);
    const collapseItems = useMemo(
        () =>
            rows.map((faq) => ({
                key: String(faq.id),
                label: (
                    <div id={`faq-${faq.id}`} className="flex scroll-mt-8 flex-col gap-2">
                        <span className="font-medium text-base text-foreground">
                            <Highlight data={faq.title} highlights={highlights} matchClassName={MATCH_CLASS_NAME} />
                        </span>
                        {faq.categories.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {faq.categories.map((category) => (
                                    <Tag key={category} variant="outlined" color="blue" className="m-0">
                                        {category}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </div>
                ),
                children: (
                    <>
                        <FaqAnswer content={faq.content} term={query} />
                        <VideoPlayer videoFileKey={faq.videoFileKey || ''} />
                    </>
                ),
            })),
        [rows, query, highlights],
    );

    const hasResults = rows.length > 0;
    const hasFilters = query !== '' || categories.length > 0;
    // Rows held on screen while a new filter loads stay there, but a held empty list answered the
    // filter it replaced: its empty state would speak for the wrong filter, so it spins instead.
    const listLoading = !hasResults && (isPending || isPlaceholderData);

    return (
        <PageController route={Route}>
            <div className="flex max-w-4xl flex-col gap-4">
                {queryError ? (
                    <InlineQueryError
                        error={queryError}
                        onRetry={() => retryFailedQueries(pageQueries)}
                        retrying={isRetryingQueries(pageQueries)}
                    />
                ) : (
                    <>
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-col gap-3 sm:flex-row">
                                <Input
                                    aria-label="Search FAQs"
                                    placeholder="Search FAQs"
                                    prefix={<Search className="h-4 w-4 text-subtle-foreground" />}
                                    value={searchText}
                                    onChange={(event) => setSearchText(event.target.value)}
                                    maxLength={FAQ_SEARCH_MAX_LENGTH}
                                    allowClear
                                    className="flex-1"
                                />
                                <Select
                                    aria-label="Filter by category"
                                    mode="multiple"
                                    placeholder="Filter by category"
                                    value={categories}
                                    onChange={(next: string[]) =>
                                        changeFilter({ category: next.length > 0 ? next : undefined })
                                    }
                                    maxCount={FAQ_MAX_CATEGORIES}
                                    options={faqCategoryOptions(categoriesQuery.data ?? NO_CATEGORIES)}
                                    allowClear
                                    className="w-full sm:w-64"
                                    maxTagCount="responsive"
                                />
                            </div>
                            {!isPending && (
                                <p className="m-0 text-muted-foreground text-sm" aria-live="polite">
                                    {total === 1 ? '1 question' : `${total} questions`}
                                    {isPlaceholderData && (
                                        <span className="ml-2 text-subtle-foreground">Updating…</span>
                                    )}
                                </p>
                            )}
                        </div>

                        <Loading spinning={listLoading} keepWrapper>
                            {hasResults ? (
                                <Collapse
                                    ref={listRef}
                                    accordion
                                    activeKey={openId ? [openId] : []}
                                    onChange={handleOpenChange}
                                    bordered={false}
                                    expandIcon={({ isActive }) => (
                                        <ChevronRight
                                            className={clsx(
                                                'h-4 w-4 text-subtle-foreground transition-transform duration-200',
                                                isActive && 'rotate-90',
                                            )}
                                        />
                                    )}
                                    className={clsx(
                                        'bg-transparent',
                                        '[&_.ant-collapse-item]:mb-3 [&_.ant-collapse-item]:overflow-hidden [&_.ant-collapse-item]:rounded-lg [&_.ant-collapse-item]:border [&_.ant-collapse-item]:border-separator [&_.ant-collapse-item]:bg-card',
                                        '[&_.ant-collapse-item:last-child]:mb-0',
                                        '[&_.ant-collapse-header]:items-start [&_.ant-collapse-header]:px-5 [&_.ant-collapse-header]:py-4',
                                        '[&_.ant-collapse-header:hover]:bg-muted',
                                        '[&_.ant-collapse-expand-icon]:mt-1',
                                        '[&_.ant-collapse-panel]:border-separator [&_.ant-collapse-panel]:border-t',
                                        '[&_.ant-collapse-body]:pt-4 [&_.ant-collapse-body]:pr-5 [&_.ant-collapse-body]:pb-5 [&_.ant-collapse-body]:pl-12',
                                    )}
                                    items={collapseItems}
                                />
                            ) : listLoading ? (
                                // Room for the spinner, which an empty wrapper would hide.
                                <div className="min-h-60" />
                            ) : (
                                <EmptyState
                                    icon={<HelpCircle />}
                                    title="No FAQs available at the moment"
                                    hasFilters={hasFilters}
                                    noMatchesTitle="No FAQs match your search criteria"
                                    onClearFilters={clearFilters}
                                />
                            )}
                        </Loading>

                        {/* Past the server's offset limit there is no next page; the count stays. */}
                        {hasResults && rows.length < total && !isPlaceholderData && (
                            <div className="flex items-center gap-3">
                                {isFetchNextPageError ? (
                                    <InlineQueryError
                                        compact
                                        title="More questions could not be loaded"
                                        error={faqsQuery.error}
                                        onRetry={() => void fetchNextPage()}
                                        retrying={isFetchingNextPage}
                                    />
                                ) : (
                                    hasNextPage && (
                                        <Button
                                            type="secondary"
                                            loading={isFetchingNextPage}
                                            onClick={() => void fetchNextPage()}
                                        >
                                            Load more questions
                                        </Button>
                                    )
                                )}
                                <span className="text-muted-foreground text-sm">
                                    Showing {rows.length} of {total}
                                </span>
                            </div>
                        )}
                    </>
                )}

                <p className="m-0 mt-4 text-muted-foreground text-sm">
                    Still need help? Email{' '}
                    <ExternalLink href={`mailto:${SUPPORT_EMAIL}`} target="_self">
                        {SUPPORT_EMAIL}
                    </ExternalLink>
                </p>
            </div>
        </PageController>
    );
}
